/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import Badge from '@material-ui/core/Badge';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import {makeStyles} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import classnames from 'classnames';
import isNumber from 'lodash.isnumber';
import {translate} from '@ion/core/src/locale/locale';
import Render from '../../../render/Render';
import {dlsColor, dlsShadow, dlsZIndex} from '../../../theme/theme';
import {MenuItem, MenuItems} from '../../../types/components';
import {FLYOUT_SUBMENU_ITEM_THRESHOLD} from '../constants';
import NavigationFlyout from './NavigationFlyout';

// defines the style props for current navigation list styles
interface StyleProps {
  collapsed?: boolean;
  footer?: boolean;
  mobile?: boolean;
}

// the list's own inset - the desktop rows' 8px sides (the footer's 8px
// below as well), the phone sidesheet's 8px all round, none collapsed ...
const getListPadding = ({collapsed, footer, mobile}: StyleProps): string => {
  let padding = footer ? '0 8px 8px' : '0 8px';
  if (collapsed) {
    padding = '0';
  } else if (mobile) {
    padding = '8px';
  }
  return padding;
};

const useStyles = makeStyles(() => ({
  list : {
    display       : 'flex',
    flexDirection : 'column',
    fontFamily    : 'Inter',
    margin        : 0,
    minHeight     : 0,
    overflow      : 'visible',
    padding       : getListPadding
  },
  subMenuSection : {
    boxSizing    : 'border-box',
    marginBottom : 0,
    marginTop    : 0,
    padding      : '0 16px 0 24px',
    width        : '100%'
  },
  // the phone sidesheet keeps its 48px rows - the condensed 44px rows are
  // the desktop nav's ...
  mobileItem : {
    '&&' : {
      minHeight : 48,
      padding   : 8
    }
  },
  mobileNestedItem : {
    '&&' : {
      minHeight : 48,
      padding   : '8px 0'
    }
  },
  // mobile Figma: active sub-item frame spans x40-224, right-aligned with
  // the parent frame (x16-224) ...
  mobileSubMenuSection : {
    paddingRight : 8
  },
  item : {
    boxSizing                    : 'border-box',
    borderRadius                 : 8,
    marginBottom                 : 0,
    // Figma: 44px rows, the 32px item frame centred in each ...
    minHeight                    : 44,
    overflow                     : 'visible',
    padding                      : '6px 8px',
    transition                   : 'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    '&.MuiListItem-button:hover' : {
      backgroundColor : 'transparent'
    },
    '&.MuiListItem-button:active' : {
      backgroundColor : 'transparent'
    },
    // hover only on pointer devices, so touch taps don't leave a sticky
    // hover frame on the mobile sidesheet ...
    '@media (hover: hover)' : {
      '&:hover $itemFrame' : {
        backgroundColor : dlsColor('white'),
        border          : `1px solid ${dlsColor('grey5')}`,
        borderRadius    : 8,
        boxShadow       : '0 1px 2px rgba(0, 0, 0, 0.12)'
      },
      '&:hover $primaryText' : {
        color : dlsColor('lightTeal')
      }
    },
    '&:active $itemFrame' : {
      backgroundColor : dlsColor('paleBlue'),
      border          : `1px solid ${dlsColor('grey5')}`,
      boxShadow       : dlsShadow(3)
    },
    // !important: the label color is set inline via primaryTypographyProps ...
    '&:active $primaryText' : {
      color : `${dlsColor('darkTeal')} !important`
    },
    '&:focus-visible' : {
      boxShadow : `inset 0 0 0 2px ${dlsColor('aaBlue')}`,
      outline   : 'none'
    }
  },
  collapsedItem : {
    justifyContent : 'center',
    minHeight      : 44,
    padding        : '6px 8px'
  },
  nestedItem : {
    display                 : 'flex',
    justifyContent          : 'flex-end',
    minHeight               : 44,
    padding                 : '6px 0',
    '@media (hover: hover)' : {
      '&:hover $itemFrame' : {
        backgroundColor : dlsColor('white'),
        border          : `1px solid ${dlsColor('grey5')}`,
        borderRadius    : 8,
        boxShadow       : dlsShadow(3),
        width           : 184
      },
      '&:hover $nestedPrimaryText' : {
        color : dlsColor('lightTeal')
      }
    },
    '&:active $itemFrame' : {
      backgroundColor : dlsColor('paleBlue2'),
      border          : `1px solid ${dlsColor('grey5')}`,
      boxShadow       : dlsShadow(3),
      width           : 184
    },
    '&:active $nestedPrimaryText' : {
      color : `${dlsColor('darkTeal')} !important`
    }
  },
  nestedItemActive : {
    backgroundColor : 'transparent'
  },
  itemFrame : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : '1px solid transparent',
    borderRadius    : 8,
    boxSizing       : 'border-box',
    display         : 'flex',
    height          : 32,
    justifyContent  : 'space-between',
    overflow        : 'visible',
    padding         : '4px 8px',
    position        : 'relative',
    width           : '100%'
  },
  taggedItemFrame : {
    gap            : 8,
    justifyContent : 'flex-start'
  },
  itemFrameActive : {
    backgroundColor : dlsColor('paleBlue'),
    border          : '1px solid transparent',
    boxShadow       : 'none'
  },
  collapsedItemFrame : {
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'center',
    width          : '100%'
  },
  nestedItemFrame : {
    marginLeft : 'auto',
    width      : 184
  },
  // applied only when `isFlyout` (NavigationFlyout.tsx's popover), not for
  // the inline-expanded submenu (subMenuSection below), which also passes
  // `nested` but should keep nestedItemFrame's own width/padding as-is -
  // overrides itemFrame's shared `padding: '4px 8px'` back to just the
  // horizontal inset, since nestedItem's own row already provides vertical
  // spacing (`padding: '8px 0'`), and drops the fixed 184px width the
  // flyout's own narrower frame doesn't need ...
  flyoutItemFrame : {
    padding : '0 8px !important'
  },
  nestedItemFrameActive : {
    backgroundColor : dlsColor('paleBlue'),
    border          : '1px solid transparent',
    boxShadow       : 'none'
  },
  iconWrapper : {
    alignItems     : 'center',
    borderRadius   : 10,
    display        : 'flex',
    justifyContent : 'center',
    marginRight    : 4,
    minWidth       : 24
  },
  collapsedIconWrapper : {
    backgroundColor : dlsColor('white'),
    height          : 32,
    justifyContent  : 'center',
    marginRight     : 0,
    minWidth        : 32,
    width           : 32
  },
  collapsedIconWrapperActive : {
    backgroundColor : dlsColor('paleBlue')
  },
  itemText : {
    flex       : 1,
    margin     : 0,
    minWidth   : 0,
    overflow   : 'hidden',
    whiteSpace : 'nowrap'
  },
  taggedItemText : {
    flex : '0 1 auto'
  },
  primaryText : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 500,
    lineHeight : '20px'
  },
  nestedPrimaryText : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 500,
    lineHeight : '20px'
  },
  secondaryText : {
    color         : dlsColor('lightTeal'),
    fontFamily    : 'Inter',
    fontSize      : 10,
    fontWeight    : 600,
    letterSpacing : '0.5px'
  },
  nestedList : {
    margin  : 0,
    padding : 0,
    width   : '100%'
  },
  // applied only when `isFlyout`, not for the inline-expanded submenu
  // (which also passes `nested` but should keep nestedList's own width/
  // padding as-is) ...
  flyoutList : {
    padding : '0 0 0 24px !important',
    width   : 'auto'
  },
  footerList : {
    width : 224
  },
  footerItem : {
    width : 224
  },
  footerItemFrame : {
    flexShrink : 0,
    width      : 208
  },
  chevronWrapper : {
    alignItems     : 'center',
    display        : 'flex',
    flexShrink     : 0,
    height         : 24,
    justifyContent : 'flex-end',
    width          : 24
  },
  chevronIcon : {
    transformOrigin : 'center',
    transition      : 'transform 0.18s ease-in-out'
  },
  betaTag : {
    alignItems      : 'flex-start',
    backgroundColor : dlsColor('betaYellow'),
    borderRadius    : 4,
    boxShadow       : '0 1px 2px rgba(0, 0, 0, 0.12)',
    boxSizing       : 'border-box',
    color           : dlsColor('grey1'),
    display         : 'inline-flex',
    flexShrink      : 0,
    fontFamily      : 'Inter',
    fontSize        : 10,
    fontWeight      : 600,
    height          : 24,
    justifyContent  : 'center',
    letterSpacing   : '0.5px',
    lineHeight      : '16px',
    padding         : 4,
    textAlign       : 'center',
    textTransform   : 'uppercase',
    width           : 36
  },
  badge : {
    alignItems          : 'center',
    display             : 'flex',
    '& .MuiBadge-badge' : {
      backgroundColor : dlsColor('red'),
      color           : dlsColor('white'),
      zIndex          : 1
    }
  },
  collapsedBadge : {
    position            : 'absolute',
    right               : -4,
    top                 : -4,
    '& .MuiBadge-badge' : {
      fontSize  : 10,
      height    : 16,
      minWidth  : 16,
      padding   : '0 4px',
      right     : 0,
      top       : 0,
      transform : 'none'
    }
  },
  footerBadge : {
    flexShrink          : 0,
    marginLeft          : 8,
    '& .MuiBadge-badge' : {
      position  : 'static',
      transform : 'none'
    }
  },
  // the collapsed menu's labels share the flyout's layer, so they stay above
  // any page scrim drawn over the content beside the menu ...
  tooltipPopper : {
    zIndex : `${dlsZIndex('navigationFlyout')} !important`
  },
  tooltip : {
    backgroundColor : 'transparent',
    marginLeft      : '8px !important',
    maxWidth        : 'none',
    padding         : '0 !important'
  },
  tooltipContent : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxShadow       : dlsShadow(3.2),
    boxSizing       : 'border-box',
    color           : dlsColor('lightTeal'),
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    gap             : 8,
    height          : 32,
    lineHeight      : '20px',
    minHeight       : 32,
    padding         : '4px 16px',
    whiteSpace      : 'nowrap'
  },
  tooltipContentActive : {
    backgroundColor : dlsColor('paleBlue2')
  }
}));

export interface NavigationListProps {
  items?: MenuItems;
  selectedIndex: number;
  activeIndex?: number;
  currentPage: string;
  collapsed?: boolean;
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem, isMainMenu?: boolean): void;
  footer?: boolean;
  // true for the current-theme mobile sidesheet (Navigation's `mobile`) ...
  mobile?: boolean;
  nested?: boolean;
  // true only for NavigationFlyout.tsx's popover - distinct from `nested`,
  // which is also true for the inline-expanded submenu (subMenuSection
  // below); some styling (list/item-frame width and padding) applies to
  // the flyout only, not both ...
  isFlyout?: boolean;
  expandedIndex?: number;
  expandedItems?: MenuItems;
  showSecondaryNavigation?: boolean;
  hasSubmenuAnchor?: boolean;
  submenuAnchor?: HTMLElement | null;
  submenuAnchorIndex?: number | null;
  suppressedCurrentSubmenuIndex?: number | null;
  onCurrentSubmenuClose?: (index: number | null) => void;
  registerSubmenuAnchor?: (index: number, element: HTMLElement | null) => void;
  onSubmenuClose?: () => void;
  onSubmenuToggle?: (anchorEl: HTMLElement | null, index: number) => void;
  useTopNavigation?: boolean;
}

/**
 * Defines the current-theme Navigation List Component. This component is intended
 * to render current-theme primary, nested, and footer navigation items for the
 * shared Navigation shell.
 *
 * ```typescript
 *
 *   <NavigationList items={menuItems}
 *     selectedIndex={selectedIndex}
 *     currentPage={currentPage}
 *     onSetPage={onSetPage}
 *   />
 *
 * ```
 */
const NavigationList = ({
  items = [],
  selectedIndex,
  activeIndex,
  currentPage,
  onSetPage,
  registerSubmenuAnchor,
  onSubmenuToggle,
  collapsed = false,
  footer = false,
  mobile = false,
  nested = false,
  isFlyout = false,
  expandedIndex = -1,
  expandedItems = [],
  showSecondaryNavigation = false,
  hasSubmenuAnchor = false,
  submenuAnchor,
  submenuAnchorIndex,
  suppressedCurrentSubmenuIndex,
  useTopNavigation = true,
  onCurrentSubmenuClose,
  onSubmenuClose
}: NavigationListProps) => {
  const classes = useStyles({collapsed, footer, mobile});

  return (
    <List className={classnames(classes.list, {
      [classes.nestedList] : nested,
      [classes.flyoutList] : isFlyout,
      [classes.footerList] : footer && !collapsed
    })}>
      {items.map((item, index) => {
        const {
          icon,
          key,
          label,
          meta,
          page,
          pages
        } = item;
        const hasChildren = !!pages?.length;
        const isExpandedParent = !nested && expandedIndex === index && !!expandedItems.length;
        const shouldUseTopNav  = useTopNavigation && !nested && hasChildren && (
          pages.length >= FLYOUT_SUBMENU_ITEM_THRESHOLD
        );
        const shouldUseFlyout      = !nested && hasChildren && collapsed && !shouldUseTopNav;
        const isFlyoutOpen         = shouldUseFlyout && submenuAnchorIndex === index && !!submenuAnchor;
        const isCurrentPageSubmenu = !nested && hasChildren && pages.some(({page}) => (
          !!page && (page === currentPage || currentPage?.startsWith(`${page}/`))
        ));
        const isDifferentSmallSubmenuOpen = (
          showSecondaryNavigation &&
          expandedIndex >= 0 &&
          activeIndex !== index
        );
        const selected = !footer && (
          selectedIndex === index ||
          page === currentPage ||
          (isCurrentPageSubmenu && !isDifferentSmallSubmenuOpen) ||
          (showSecondaryNavigation && activeIndex === index)
        );
        const shouldPersistCurrentSubmenu = (
          isCurrentPageSubmenu &&
          suppressedCurrentSubmenuIndex !== index
        );
        const isCurrentSubmenuSuppressed = (
          isCurrentPageSubmenu &&
          suppressedCurrentSubmenuIndex === index
        );
        const showInlineExpandedChildren = (
          !collapsed &&
          !isCurrentSubmenuSuppressed &&
          (isExpandedParent || shouldPersistCurrentSubmenu) &&
          !shouldUseTopNav &&
          !shouldUseFlyout
        );
        const inlineExpandedItems = isCurrentPageSubmenu ? pages : expandedItems;
        // mobile Figma: when an expanded child is the current page, the parent
        // keeps its teal label/icon but drops the active background ...
        const showActiveFrame = selected && !(
          mobile && showInlineExpandedChildren && isCurrentPageSubmenu
        );

        const openCollapsedFlyout = function(event) {
          if (shouldUseFlyout) {
            onSubmenuToggle?.(event.currentTarget, index);
            return;
          }
          if (collapsed && !nested && !footer) {
            onSubmenuClose?.();
          }
        };

        const onClick = function(event) {
          if (hasChildren && !nested && !footer) {
            if (!collapsed && shouldPersistCurrentSubmenu) {
              onCurrentSubmenuClose?.(index);
              return;
            }
            if (!collapsed && isCurrentPageSubmenu && suppressedCurrentSubmenuIndex === index) {
              onCurrentSubmenuClose?.(null);
              return;
            }
            if (shouldUseFlyout) {
              onSubmenuToggle?.(
                showSecondaryNavigation && expandedIndex === index ? null : event.currentTarget,
                index
              );
            } else if (page) {
              onSetPage(page, index, false, item, false);
            }
            return;
          }
          if (meta?.onClick) {
            meta.onClick();
            return;
          }
          if (page) {
            onSetPage(page, index, nested, item, !nested);
          }
        };
        function onInlineSubmenuSetPage(...args: Parameters<NavigationListProps['onSetPage']>) {
          const [childPage,, isSecondaryIndex, childItem] = args;
          onSetPage(childPage, index, isSecondaryIndex, childItem, false);
        }

        const listItem = (
          <React.Fragment key={key ?? label}>
            <ListItem button={!!(meta?.onClick || page)}
              disableRipple
              disableTouchRipple
              onClick={onClick}
              onMouseEnter={openCollapsedFlyout}
              onFocus={openCollapsedFlyout}
              ref={!nested && !footer ? (element) => registerSubmenuAnchor?.(index, element) : undefined}
              aria-label={collapsed ? label : undefined}
              className={classnames(classes.item, {
                [classes.collapsedItem]    : collapsed,
                [classes.footerItem]       : footer && !collapsed,
                [classes.nestedItem]       : nested,
                [classes.nestedItemActive] : selected && nested,
                [classes.mobileItem]       : mobile && !nested,
                [classes.mobileNestedItem] : mobile && nested
              })}>
              <div className={classnames(classes.itemFrame, {
                [classes.itemFrameActive]       : showActiveFrame && !nested,
                [classes.collapsedItemFrame]    : collapsed,
                [classes.footerItemFrame]       : footer && !collapsed,
                [classes.nestedItemFrame]       : nested,
                [classes.flyoutItemFrame]       : isFlyout,
                [classes.nestedItemFrameActive] : selected && nested,
                [classes.taggedItemFrame]       : !collapsed && !!meta?.tag?.label
              })}>
                <Render.If condition={!!icon && !nested}>
                  <ListItemIcon className={classnames(classes.iconWrapper, {
                    [classes.collapsedIconWrapper]       : collapsed,
                    [classes.collapsedIconWrapperActive] : collapsed && selected
                  })}>
                    {React.isValidElement(icon)
                      ? React.cloneElement(icon as React.ReactElement<any>, {
                        color : selected ? dlsColor('darkTeal') : dlsColor('grey1')
                      })
                      : icon}
                  </ListItemIcon>
                </Render.If>
                <Render.If condition={!collapsed}>
                  <ListItemText primary={label}
                    secondary={meta?.subtitle}
                    className={classnames(classes.itemText, {
                      [classes.taggedItemText] : !!meta?.tag?.label
                    })}
                    classes={{
                      primary   : nested ? classes.nestedPrimaryText : classes.primaryText,
                      secondary : classes.secondaryText
                    }}
                    primaryTypographyProps={{
                      style : {
                        color : selected ? dlsColor('darkTeal') : dlsColor('grey1')
                      }
                    }} />
                </Render.If>
                <Render.If condition={!collapsed && !!meta?.tag?.label}>
                  <span className={classes.betaTag}>
                    {translate(meta?.tag?.label)}
                  </span>
                </Render.If>
                <Render.If condition={isNumber(meta?.badgeCount)}>
                  <Badge badgeContent={meta?.badgeCount}
                    color='secondary'
                    className={classnames(classes.badge, {
                      [classes.collapsedBadge] : collapsed,
                      [classes.footerBadge]    : footer && !collapsed
                    })} />
                </Render.If>
                <Render.If condition={hasChildren && !shouldUseTopNav && !meta?.hideChevron && !collapsed}>
                  <div className={classes.chevronWrapper}>
                    <ChevronRightIcon htmlColor={selected ? dlsColor('darkTeal') : dlsColor('grey1')}
                      className={classes.chevronIcon}
                      style={{transform: `rotate(${showInlineExpandedChildren ? 90 : 0}deg)`}} />
                  </div>
                </Render.If>
              </div>
            </ListItem>
            <Render.If condition={showInlineExpandedChildren}>
              <div className={classnames(classes.subMenuSection, {
                [classes.mobileSubMenuSection] : mobile
              })}>
                <NavigationList items={inlineExpandedItems}
                  classes={classes}
                  selectedIndex={-1}
                  currentPage={currentPage}
                  nested
                  onSetPage={onInlineSubmenuSetPage} />
              </div>
            </Render.If>
            <Render.If condition={isFlyoutOpen}>
              <NavigationFlyout activeLabel={label}
                anchorEl={submenuAnchor}
                currentPage={currentPage}
                onClose={onSubmenuClose}
                onSetPage={onSetPage}
                parentIndex={index}
                secondaryMenuItems={pages}
                visible={isFlyoutOpen} />
            </Render.If>
          </React.Fragment>
        );
        const shouldRenderCollapsedTooltip = collapsed && !shouldUseFlyout;

        return (
          <Render>
            <Render.If condition={shouldRenderCollapsedTooltip}>
              <Tooltip key={key ?? label}
                title={(
                  <div className={classnames(classes.tooltipContent, {
                    [classes.tooltipContentActive] : selected
                  })}>
                    <span>{label}</span>
                  </div>
                )}
                placement='right'
                enterDelay={0}
                leaveDelay={0}
                classes={{
                  popper  : classes.tooltipPopper,
                  tooltip : classes.tooltip
                }}>
                <div>{listItem}</div>
              </Tooltip>
            </Render.If>
            <Render.Else>
              {listItem}
            </Render.Else>
          </Render>
        );
      })}
    </List>
  );
};

export default memo(NavigationList);
